import { useEffect, useState } from 'react'
import './FashionProductCard.css'
import { useProductCard } from '../shared/useProductCard'
import {
  FlyToCartBubbles,
  ProductDetailScreen,
  VariantPickerPortal,
} from '../shared/ProductCardPortals'

/**
 * Fashion (Zara/Bershka-style) catalog card: edge-to-edge portrait image,
 * second image on desktop hover, tap zones + dots on touch devices,
 * minimal uppercase typography. Logic comes from useProductCard.
 */
function FashionProductCard({ product, onAddToCart, cartCount = 0 }) {
  const card = useProductCard(product, { onAddToCart, cartCount })
  const {
    productImages,
    coverImage,
    currentPrice,
    oldPrice,
    pricePrefix,
    priceSuffix,
    isAnimating,
    handleAddClick,
    detailOpen,
    openDetail,
  } = card

  const [cardImageIndex, setCardImageIndex] = useState(0)

  useEffect(() => {
    setCardImageIndex(0)
  }, [product?.id, product?._id])

  const imageCount = productImages.length
  const hasMultipleImages = imageCount > 1
  const safeCardIndex = hasMultipleImages
    ? ((cardImageIndex % imageCount) + imageCount) % imageCount
    : 0
  const displayedImage = productImages[safeCardIndex]?.url ?? coverImage
  const secondImage = hasMultipleImages ? productImages[1]?.url : null

  const stepCardImage = (delta) => (e) => {
    e.stopPropagation()
    e.preventDefault()
    if (!hasMultipleImages) return
    setCardImageIndex((index) => (index + delta + imageCount) % imageCount)
  }

  return (
    <div className="fashion-card">
      <div className="fashion-card-media-wrap">
        <button
          type="button"
          className="fashion-card-media"
          onClick={openDetail}
          aria-haspopup="dialog"
          aria-expanded={detailOpen}
          aria-label={`Подробнее: ${product.name ?? 'товар'}`}
        >
          <img
            src={displayedImage}
            alt=""
            className="fashion-card-image"
            draggable={false}
          />
          {secondImage ? (
            <img
              src={secondImage}
              alt=""
              className="fashion-card-image fashion-card-image--second"
              draggable={false}
              aria-hidden="true"
            />
          ) : null}
        </button>
        {hasMultipleImages ? (
          <>
            <button
              type="button"
              className="fashion-card-tap fashion-card-tap--prev"
              onClick={stepCardImage(-1)}
              aria-label="Предыдущее фото"
              tabIndex={-1}
            />
            <button
              type="button"
              className="fashion-card-tap fashion-card-tap--next"
              onClick={stepCardImage(1)}
              aria-label="Следующее фото"
              tabIndex={-1}
            />
            <div className="fashion-card-dots" aria-hidden="true">
              {productImages.map((img, i) => (
                <span
                  key={`${img.url}-${i}`}
                  className={`fashion-card-dot${i === safeCardIndex ? ' fashion-card-dot--active' : ''}`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
      <div className="fashion-card-info">
        <button
          type="button"
          className="fashion-card-name"
          onClick={openDetail}
          aria-haspopup="dialog"
          aria-expanded={detailOpen}
        >
          {product.name}
        </button>
        <div className="fashion-card-price-row">
          <span className="fashion-card-price">
            {Number.isFinite(currentPrice)
              ? `${pricePrefix}${currentPrice.toFixed(2)} сом${priceSuffix || ''}`
              : '—'}
          </span>
          {oldPrice != null && (
            <span className="fashion-card-price-old" aria-label="Прежняя цена">
              {oldPrice.toFixed(2)} сом
            </span>
          )}
        </div>
        <button
          type="button"
          className={`fashion-card-add${isAnimating ? ' fashion-card-add--animating' : ''}`}
          onClick={handleAddClick}
          aria-label="В корзину"
        >
          <span>В корзину</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M10 4V16M4 10H16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <ProductDetailScreen skin="fashion" card={card} />
      <VariantPickerPortal card={card} />
      <FlyToCartBubbles items={card.flyItems} />
    </div>
  )
}

export default FashionProductCard
